import { computed } from "vue";

import type { NavigationItem } from "@/composition/shared/useNavigation";
import { useUserStore } from "@/stores/user.ts";
import type { Project } from "@/types/globalTypes";
import { DpaServices } from "@/utils/content.ts";

interface AvailableNavigationItem extends NavigationItem {
  allowed: boolean;
}

type PermissionType =
  | "content"
  | "newsfeed"
  | "savedSearch"
  | "desk"
  | "comment"
  | "message"
  | "cvd"
  | "AI-Research"
  | "infokanal"
  | "intelligence_profile";

type PermissionActions =
  | "read"
  | "write"
  | "delete"
  | "download"
  | "create"
  | "reply"
  | "search_unlimited";

const useAuthorization = (userStore = useUserStore()) => {
  const isUserPermittedForAction = (permissionQuery: {
    type: PermissionType;
    action: PermissionActions;
    value?: string[] | number | string;
  }) => {
    const permissions = userStore?.userPermissions?.permissions;
    if (!Array.isArray(permissions)) {
      return false;
    }

    const permission = permissions.find(
      (permission) => permission.type === permissionQuery.type,
    );
    if (!permission) {
      return false;
    }
    const action = permission.actions.find(
      (actions) => actions.action === permissionQuery.action,
    );
    const selectors = action?.selectors;
    if (
      selectors &&
      selectors.length > 0 &&
      permissionQuery?.value !== undefined
    ) {
      return selectors.every((selector) => {
        let hasPermission = true;
        if (
          selector.type === "contains" &&
          Array.isArray(permissionQuery.value)
        ) {
          hasPermission = selector.values?.some((value) =>
            (permissionQuery.value as string[]).includes(value as string),
          );
        }
        if (
          selector.type === "less_than" &&
          typeof permissionQuery.value === "number"
        ) {
          hasPermission = permissionQuery.value < Number(selector.values?.[0]);
        }
        return hasPermission;
      });
    }
    return !!action;
  };

  const isPermittedForNewsfeed = (): boolean => {
    return isUserPermittedForAction({
      type: "newsfeed",
      action: "read",
    });
  };

  const isPermittedForDesk = (): boolean => {
    return isUserPermittedForAction({
      type: "desk",
      action: "read",
    });
  };

  const isPermittedForMessenger = (): boolean => {
    return isUserPermittedForAction({
      type: "message",
      action: "reply",
    });
  };

  const isPermittedForCvd = (): boolean => {
    return isUserPermittedForAction({
      type: "cvd",
      action: "read",
    });
  };

  const isPermittedForAiResearch = (): boolean => {
    return isUserPermittedForAction({
      type: "AI-Research",
      action: "read",
    });
  };

  const isPermittedForIntelligence = (): boolean => {
    return isUserPermittedForAction({
      type: "intelligence_profile",
      action: "read",
    });
  };

  const isPermittedForIntelligenceWrite = (): boolean => {
    return isUserPermittedForAction({
      type: "intelligence_profile",
      action: "create",
    });
  };

  const isPermittedForIntelligenceDelete = (): boolean => {
    return isUserPermittedForAction({
      type: "intelligence_profile",
      action: "delete",
    });
  };

  const getIntelligenceMaxProfiles = (): number | null => {
    const permissions = userStore?.userPermissions?.permissions;
    if (!Array.isArray(permissions)) return null;

    const createAction = permissions
      .find((p) => p.type === "intelligence_profile")
      ?.actions.find((a) => a.action === "create");

    const selector = createAction?.selectors?.find(
      (s) => s.property === "num_resources",
    );
    if (!selector?.values?.[0]) return null;
    return Number(selector.values[0]);
  };

  const canCreateIntelligenceProfile = (currentProfiles: number): boolean => {
    return isUserPermittedForAction({
      type: "intelligence_profile",
      action: "create",
      value: currentProfiles,
    });
  };

  const hasSearchUnlimited = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "search_unlimited",
    });
  };

  const hasInfokanal = computed((): boolean => {
    return isUserPermittedForAction({
      type: "infokanal",
      action: "read",
    });
  });

  const hasBasisDienst = () => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.BASISDIENST],
    });
  };

  const hasBildfunkDienst = computed((): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.BILDFUNK],
    });
  });

  const isPermittedForLandesdienst = (landesdienst: DpaServices) => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [landesdienst],
    });
  };

  const hasLandesdienstSachsenAnhalt = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.SACHSEN_ANHALT],
    });
  };

  const hasLandesdienstSachsen = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.SACHSEN],
    });
  };

  const hasLandesdienstBerlin = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.BERLIN_BRANDENBURG],
    });
  };

  const hasLandesdienstBayern = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.BAYERN],
    });
  };

  const hasLandesdienstHessen = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.HESSEN],
    });
  };

  const hasLandesdienstMecklenburg = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.MECKLENBURG_VORPOMMERN],
    });
  };

  const hasLandesdienstNiedersachsen = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.NIEDERSACHSEN_BREMEN],
    });
  };

  const hasLandesdienstHamburgSH = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.HAMBURG_SCHLESWIG_HOLSTEIN],
    });
  };

  const hasLandesdienstNRW = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.NORDRHEIN_WESTFALEN],
    });
  };

  const hasLandesdienstRheinland = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.RHEINLAND_PFALZ_SAARLAND],
    });
  };

  const hasLandesdienstBaWü = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.BADEN_WUERTTEMBERG],
    });
  };

  const hasLandesdienstThüringen = (): boolean => {
    return isUserPermittedForAction({
      type: "content",
      action: "read",
      value: [DpaServices.THUERINGEN],
    });
  };

  const hasAnyLandesdienst = () => {
    const landesdienstChecks = [
      hasLandesdienstRheinland(),
      hasLandesdienstThüringen(),
      hasLandesdienstBaWü(),
      hasLandesdienstHamburgSH(),
      hasLandesdienstMecklenburg(),
      hasLandesdienstNiedersachsen(),
      hasLandesdienstSachsen(),
      hasLandesdienstHessen(),
      hasLandesdienstSachsenAnhalt(),
      hasLandesdienstBayern(),
      hasLandesdienstBerlin(),
      hasLandesdienstNRW(),
    ];

    return landesdienstChecks.some((value) => value === true);
  };

  const permittedApps = (): Project[] => {
    const availableApps: Project[] = ["archive"];
    if (isPermittedForCvd()) {
      availableApps.push("cvd");
    }
    if (isPermittedForAiResearch()) {
      availableApps.push("ai-research");
    }
    if (isPermittedForDesk()) {
      availableApps.push("desk");
    }
    if (isPermittedForMessenger()) {
      availableApps.push("messenger");
    }
    if (isPermittedForIntelligence()) {
      availableApps.push("intelligence");
    }

    return availableApps;
  };
  const getPermittedApps = (
    navigationItems: NavigationItem[],
  ): AvailableNavigationItem[] => {
    const apps = permittedApps();
    return navigationItems.map((item) => {
      return {
        ...item,
        allowed: apps.includes(item.name),
      };
    });
  };

  return {
    isPermittedForNewsfeed,
    isPermittedForDesk,
    isPermittedForCvd,
    isPermittedForMessenger,
    isPermittedForAiResearch,
    isUserPermittedForAction,
    getPermittedApps,
    permittedApps,
    hasSearchUnlimited,
    hasInfokanal,
    hasBasisDienst,
    hasBildfunkDienst,
    isPermittedForLandesdienst,
    hasAnyLandesdienst,
    hasLandesdienstSachsenAnhalt,
    hasLandesdienstBerlin,
    hasLandesdienstBayern,
    hasLandesdienstHessen,
    hasLandesdienstMecklenburg,
    hasLandesdienstNiedersachsen,
    hasLandesdienstHamburgSH,
    hasLandesdienstNRW,
    hasLandesdienstRheinland,
    hasLandesdienstBaWü,
    hasLandesdienstThüringen,
    hasLandesdienstSachsen,
    isPermittedForIntelligence,
    isPermittedForIntelligenceWrite,
    isPermittedForIntelligenceDelete,
    canCreateIntelligenceProfile,
    getIntelligenceMaxProfiles,
  };
};

export default useAuthorization;
